import instance from "lib/axiosInterceptor";
import {
  BookingsSetUpPayload,
  ConfirmBookingPayload,
  GetBookingsParams,
  GetBookingsResponse,
  GetMyBookingsParams,
  GetMyBookingsResponse,
  InviteToSpeakPayload,
  SpeakingInvitation,
  SpeakingInvitationStatus,
  GetSpeakingInvitationsParams,
  SpeakingInvitationsResponse,
  SpeakingInvitationDetails,
  SpeakingInvitationDetailsResponse,
  SendCorrespondence,
  GetCorrespondence,
  RespondToInvitation,
  BookingInquiryPayload,
  BookingProfileImage,
  BookingByReferenceResponse,
  CreateBookingPayload,
  CreateBookingResponse,
  FetchedCalenderInterface,
  GetBookingsResponseData,
} from "interfaces";
import { ProjectorSetting } from "interfaces/interfaces-data";

/**
 * GET REQUESTS
 */

// Get all booking calendars
export async function getBookingCalendarsService(
  params: GetBookingsParams = {},
): Promise<GetBookingsResponse> {
  const normalizedParams: GetBookingsParams =
    params.startDate || params.endDate
      ? { ...params, range: 5 }
      : { ...params };

  Object.keys(normalizedParams).forEach((key) => {
    if (normalizedParams[key as keyof GetBookingsParams] === undefined) {
      delete normalizedParams[key as keyof GetBookingsParams];
    }
  });

  const response = await instance.get<GetBookingsResponse>(
    "/Bookings/Calendar",
    { params: normalizedParams },
  );
  return response.data;
}

// Get one page of calendars for a public booking page. Public endpoints have
// returned both enveloped and unwrapped payloads, so normalize at the service
// boundary and keep components independent of the transport shape.
export async function getPublicBookingCalendarsService(
  params: GetBookingsParams,
): Promise<GetBookingsResponseData> {
  const response = await instance.get<
    GetBookingsResponse | GetBookingsResponseData
  >("/Bookings/Calendar", { params });
  const payload = response.data;

  if (Array.isArray(payload.data)) {
    return payload as GetBookingsResponseData;
  }

  return (payload as GetBookingsResponse).data;
}

// Get a single public calendar and its already-booked slots.
export async function getPublicBookingCalendarService(
  username: string,
  bookingId: string,
): Promise<FetchedCalenderInterface> {
  const response = await instance.get<
    FetchedCalenderInterface | { data: FetchedCalenderInterface }
  >("/Bookings/Calendar", {
    params: {
      username,
      id: bookingId,
      range: 0,
      pageLimit: 100,
    },
  });

  return "data" in response.data ? response.data.data : response.data;
}

export async function getPublicBookingPageService(
  username: string,
): Promise<ProjectorSetting> {
  const response = await instance.get<ProjectorSetting>("/Custom/Page", {
    params: { username },
  });
  return response.data;
}

export async function getPublicBookingProfileImageService(
  username: string,
): Promise<BookingProfileImage | null> {
  const response = await instance.get<ArrayBuffer | undefined>(
    `/Account/UserImage/${encodeURIComponent(username)}`,
    {
      params: { speakerProfile: true },
      responseType: "arraybuffer",
      suppressStatuses: [400],
      suppressMessages: ["Display picture not found"],
      returnEmptyOnSuppress: true,
    },
  );

  if (!response.data || response.data.byteLength === 0) {
    return null;
  }

  return {
    buffer: response.data,
    contentType: String(response.headers?.["content-type"] || "image/jpeg"),
  };
}

// Anonymous lookup used after a payment provider redirects the booker back to
// the public booking page. The backend verifies the payment with the provider
// before responding, so `paymentMade` here is authoritative — never trust the
// `status` query param the provider puts in the URL.
export async function getBookingByReferenceService(
  reference: string,
): Promise<BookingByReferenceResponse> {
  const response = await instance.get<BookingByReferenceResponse>(
    `/Bookings/ByReference/${encodeURIComponent(reference)}`,
  );
  return response.data;
}

// Get paginated "my bookings" list
export async function getMyBookingsService(
  params: GetMyBookingsParams = {},
): Promise<GetMyBookingsResponse> {
  const normalizedParams: GetMyBookingsParams =
    params.startDate || params.endDate
      ? {
          ...params,
          range: 5,
          startDate: params.startDate ?? "2020-01-01T00:00:00.000Z",
          endDate: params.endDate ?? new Date(new Date().setFullYear(new Date().getFullYear() + 100)).toISOString(),
        }
      : { ...params };

  Object.keys(normalizedParams).forEach((key) => {
    if (normalizedParams[key as keyof GetMyBookingsParams] === undefined) {
      delete normalizedParams[key as keyof GetMyBookingsParams];
    }
  });

  const response = await instance.get<GetMyBookingsResponse>(
    "/Bookings/Paginated",
    { params: normalizedParams },
  );
  return response.data;
}

/**
 * POST REQUESTS
 */

// Create a new booking calendar
export async function createBookingCalendarService(
  bookingData: BookingsSetUpPayload,
): Promise<string> {
  const response = await instance.post<string>(
    "/Bookings/Calendar",
    bookingData,
  );
  return response.data;
}

// Reserve a public booking slot and initialize its configured payment flow.
export async function createBookingService(
  booking: CreateBookingPayload,
): Promise<CreateBookingResponse> {
  const response = await instance.post<CreateBookingResponse>(
    "/Bookings",
    booking,
  );
  return response.data;
}

export async function sendBookingInquiryService(
  inquiry: BookingInquiryPayload,
): Promise<void> {
  await instance.post("/bookings/inquiries", inquiry);
}

// Confirm a booking
export async function confirmBookingService(
  bookingId: string,
  payload: ConfirmBookingPayload,
): Promise<void> {
  await instance.post(`/Bookings/confirm/${bookingId}`, payload);
}

// cancel a booking slot
export async function cancelBookingSlotService(slotId: string): Promise<void> {
  await instance.put(`/Bookings/cancel/${slotId}`);
}

// Send meeting reminder
export async function sendMeetingReminderService(
  slotId: string,
): Promise<void> {
  await instance.post(`/Bookings/SendReminder/${slotId}`);
}

// Invite speaker to speak
export async function inviteToSpeakService(
  speakerId: string,
  payload: InviteToSpeakPayload,
): Promise<{ invitationId: string }> {
  const result = await instance.post(
    `/Bookings/InviteToSpeak/${speakerId}`,
    payload,
  );
  return result.data;
}

export const getSpeakingInvitationService = async (
  params: GetSpeakingInvitationsParams = {},
): Promise<SpeakingInvitationsResponse> => {
  const result = await instance.get(`/Bookings/Invitations`, {
    params: {
      Page: params.page,
      PageLimit: params.pageLimit,
      Query: params.query,
      StartDate: params.startDate,
      EndDate: params.endDate,
      Status: params.status,
      sortDirection: params.sortDirection,
      // Range 5 is "custom range" and requires both dates; with none supplied
      // fall back to 0 ("last {PageLimit}") so the list loads unfiltered.
      Range: params.startDate && params.endDate ? 5 : 0,
    },
  });

  const payload = result.data?.data ?? result.data;
  const listData = payload?.items ?? payload?.data ?? payload;
  const items: SpeakingInvitation[] = Array.isArray(listData)
    ? listData.map((item: SpeakingInvitation & { status: number | string }) => ({
        ...item,
        status: mapInvitationStatus(item.status),
      }))
    : [];

  return {
    items,
    pagination: payload?.pagination,
  };
};

export const getSpeakingInvitationDetailsService = async (
  id: string,
): Promise<SpeakingInvitationDetails> => {
  const result = await instance.get<
    SpeakingInvitationDetailsResponse | { data: SpeakingInvitationDetailsResponse }
  >(`/Bookings/Invitations/${id}`);
  const responseData = result.data;
  const payload = "data" in responseData ? responseData.data : responseData;

  return {
    ...payload,
    status: mapInvitationStatus(payload.status),
  };
};

function mapInvitationStatus(status: number | string): SpeakingInvitationStatus {
  if (status === 0 || status === "0" || status === "Pending") {
    return SpeakingInvitationStatus.Pending;
  }

  if (status === 1 || status === "1" || status === "Accepted") {
    return SpeakingInvitationStatus.Accepted;
  }

  if (status === 2 || status === "2" || status === "Declined") {
    return SpeakingInvitationStatus.Declined;
  }

  return SpeakingInvitationStatus.Pending;
}

export const getSpeakingCorrespondenceService = async (
  id: string,
): Promise<GetCorrespondence[]> => {
  const result = await instance.get(
    `/Bookings/Invitation/Correspondence/${id}`,
  );
  return result.data;
};

export const sendCorrespondenceService = async (
  payload: SendCorrespondence,
) => {
  const result = await instance.post(
    `/Bookings/Invitation/Correspondence/${payload.id}`,
    { message: payload.message },
  );
  return result.data.data;
};

export const respondToInvitationService = async (
  payload: RespondToInvitation,
) => {
  const result = await instance.put(
    `/Bookings/Invitation/Accept/${payload.id}?status=${encodeURIComponent(
      payload.status,
    )}`,
  );
  return result.data;
};
